import client from "services/ngage/api-client";
import {Organisation, OrganisationData} from "types/Organisation";
import deserializeOrganisation from "services/ngage/deserializers/organisation";
import DataResource from "types/DataResource";
import {snakeKeys} from "services/array";

type OrganisationFilters = {
    search?: string | null
    page?: number
    perPage?: number
}

/**
 * Get all organisations (paginated)
 */
const filtered = async (filters: OrganisationFilters = {}) => {
    const params = Object.assign({page: 1, per_page: 100}, snakeKeys(filters))

    try {
        const page = (await client.get<DataResource<Organisation> | null>('/organisations', {params})).data

        if (page) page.data = page.data.map(deserializeOrganisation)

        return page
    } catch (error) {
        console.error("Unable to fetch organisations", error)
        return null
    }
}

/**
 * Get a single organisation
 *
 * @param {number} organisationId
 */
const get = async (organisationId: number) => {
    try {
        const organisation = (await client.get<Organisation | null>(`/organisations/${organisationId}`)).data
        return organisation ? deserializeOrganisation(organisation) : null
    } catch (error) {
        console.error(`Unable to fetch organisation ${organisationId}`, error)
        return null
    }
}

/**
 * Store a new organisation
 *
 * @param data
 */
const store = async (data: OrganisationData) => {
    try {
        const organisation = (await client.post<Organisation | null>('/organisations', data)).data
        return organisation ? deserializeOrganisation(organisation) : null
    } catch (error) {
        console.error("Unable to store organisation", error)
        throw error
    }
}

/**
 * Update an existing organisation
 *
 * @param {number} organisationId
 * @param data
 */
const update = async (organisationId: number, data: OrganisationData) => {
    try {
        const organisation = (await client.put<Organisation | null>(`/organisations/${organisationId}`, data)).data
        return organisation ? deserializeOrganisation(organisation) : null
    } catch (error) {
        console.error(`Unable to update organisation ${organisationId}`, error)
        throw error
    }
}

/**
 * Permanently delete a organisation
 *
 * @param {number} organisationId
 */
const destroy = async (organisationId: number) => {
    try {
        return (await client.delete<null>(`/organisations/${organisationId}`)).status === 204
    } catch (error) {
        console.error(`Unable to delete organisation ${organisationId}`, error)
        throw error
    }
}

const organisations = {
    filtered,
    get,
    store,
    update,
    destroy
}

export default organisations;